import { useQuery } from '@tanstack/react-query';

import { supabase } from '@/lib/supabase';
import type { GrandPrizeContent } from '@/lib/grandPrize';

export interface CompetitionSummary {
	Id: string;
	Slug: string;
	Name: string;
	Description: string | null;
	// Optional because the list query below does not select them — only
	// `useCompetition` (which selects *) returns them. Declaring them as
	// always-present made the browse list look like it had a brief to show.
	Brief?: string | null;
	BannerUrl: string | null;
	Status: 'open' | 'judging' | 'announced' | 'archived';
	SubmissionOpenAt: string;
	SubmissionCloseAt: string;
	AnnouncementAt: string | null;
	AllowedEntityTypes: string[];
	GameSystemFilter: string | null;
	MaxEntriesPerUser: number;
	MaxTotalEntries: number | null;
	// Authored in Loomlite Admin (LL-418c). Absent until the column ships;
	// `resolveGrandPrize` supplies a default in the meantime.
	GrandPrize?: GrandPrizeContent | null;
}

export function useCompetitions() {
	return useQuery({
		queryKey: ['competition-list'],
		queryFn: async (): Promise<CompetitionSummary[]> => {
			const { data, error } = await supabase
				.from('Competitions')
				.select(
					'Id, Slug, Name, Description, BannerUrl, Status, SubmissionOpenAt, SubmissionCloseAt, AnnouncementAt, AllowedEntityTypes, GameSystemFilter, MaxEntriesPerUser, MaxTotalEntries',
				)
				.in('Status', ['open', 'judging', 'announced'])
				.order('SubmissionOpenAt', { ascending: false });
			if (error) throw error;
			return (data as CompetitionSummary[] | null) ?? [];
		},
	});
}

export function useCompetition(slug: string | undefined) {
	return useQuery({
		queryKey: ['competition', slug],
		enabled: Boolean(slug),
		queryFn: async (): Promise<CompetitionSummary | null> => {
			const { data, error } = await supabase
				.from('Competitions')
				.select('*')
				.eq('Slug', slug!)
				.maybeSingle();
			if (error) throw error;
			return data as CompetitionSummary | null;
		},
	});
}
